/**
 * src/lib/useTestimonials.js
 *
 * Fetches /api/testimonials once per page load and shares the result.
 *
 * The homepage renders both the video wall and the written reviews, and the
 * results page renders all of them again. Without the module-level cache each
 * component issues its own request for the same ~60 KB payload.
 *
 * The endpoint is CMS-backed and falls back to the constants in
 * backend/server.py when the collections are empty, so this never returns
 * nothing on a fresh database. A failed request is not an error state worth
 * showing the visitor — the sections simply render nothing, and the page still
 * makes its argument without them.
 */

import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { written: [], video: [] };

/** Shared across every consumer for the lifetime of the page. */
let cache = null;
let inflight = null;

function load() {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;

  inflight = axios
    .get(`${API}/testimonials`)
    .then(({ data }) => {
      cache = {
        written: Array.isArray(data?.written) ? data.written : [],
        video: Array.isArray(data?.video) ? data.video : [],
      };
      return cache;
    })
    .catch((err) => {
      console.warn("[useTestimonials] fetch failed:", err?.message || err);
      // Deliberately NOT cached: a transient network failure on one page
      // should not blank the testimonials for the rest of the session.
      return EMPTY;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

export default function useTestimonials() {
  const [data, setData] = useState(cache || EMPTY);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let live = true;
    load().then((result) => {
      if (!live) return;
      setData(result);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, []);

  return { ...data, loading };
}
